import { useCallback, useEffect, useRef, useState } from "react";
import { useAppSelector } from "@/store/hooks";
import { shiftRequestApi } from "@/features/shift-request/api";
import type { ShiftRequest } from "@/features/shift-request/types";

export type RequestFilter = "all" | "pending" | "approved" | "rejected";

const PAGE_SIZE = 20;

export function useMyRequests(filter: RequestFilter) {
  const userId = useAppSelector((s) => s.user.userId) as string | undefined;
  const [items, setItems] = useState<ShiftRequest[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const inFlightRef = useRef(false);

  const fetchPage = useCallback(
    async (p: number) => {
      if (!userId) return;
      if (inFlightRef.current) return; // 防并发重复请求
      inFlightRef.current = true;
      setLoading(true);
      try {
        const filters: Record<string, any> = {
          requester_id: { type: "string", operator: "equals", value: userId },
        };
        if (filter !== "all") {
          filters.status = { type: "string", operator: "equals", value: filter };
        }
        const res: any = await shiftRequestApi.searchShiftRequests({
          filters,
          // 不传 columns 后端只回模块"最小字段集"(SEARCH_AND_LIST_PARAMETER_GUIDE
          // §columns),reason/payload/schedule 关系全缺失 → 显式投影表格所需字段
          columns:
            "id requestType status reason reviewerNotes payload scheduleId createdAt " +
            "schedule.scheduleDate schedule.startAt schedule.endAt schedule.shiftTypeCode",
          orderBy: "-createdAt", page: p, pageSize: PAGE_SIZE,
        });
        const results: ShiftRequest[] = Array.isArray(res?.results) ? res.results : [];
        setItems((prev) => (p === 1 ? results : [...prev, ...results]));
        setTotal(res?.pagination?.totalResults ?? results.length);
        setPage(p);
      } catch (err) {
        console.error("[my-shift] my requests load failed", { userId, filter, err });
        if (p === 1) setItems([]);
      } finally {
        setLoading(false);
        inFlightRef.current = false;
      }
    },
    [userId, filter],
  );

  useEffect(() => { void fetchPage(1); }, [fetchPage]);

  const loadMore = useCallback(() => { void fetchPage(page + 1); }, [fetchPage, page]);
  const refresh = useCallback(() => { void fetchPage(1); }, [fetchPage]);

  return { items, total, loading, hasMore: items.length < total, loadMore, refresh };
}
